import React, { useEffect, useState, useCallback, useRef } from 'react';
import { medicaoService, cleanStatusMedicaoName } from '../services/medicaoService';
import { financeiroService } from '../services/financeiroService';
import { supabase } from '../lib/supabase';
import { Medicoes, MedicoesAgregado, StatusMedicao } from '../types/medicao';
import { Plus, Search, FileSpreadsheet, CheckCircle, CheckCircle2, Clock, Edit, Trash2, DollarSign, Filter, RefreshCw, Receipt, Download, Loader2 } from 'lucide-react';
import { MedicaoFormModal } from '../components/medicao/MedicaoFormModal';
import { MedicaoAgregadoFormModal } from '../components/medicao/MedicaoAgregadoFormModal';
import { Pagination } from '../components/common/Pagination';
import { FiltroEClassificacaoBar, FilterSortState } from '../components/common/FiltroEClassificacaoBar';
import { ConfirmDeleteModal } from '../components/financeiro/ConfirmDeleteModal';
import { GerarFinanceiroModal } from '../components/medicao/GerarFinanceiroModal';
import { Toast, ToastData } from '../components/common/Toast';
import { ProtectedField, MaskedValue } from '../components/common/ProtectedField';
import { formatDateBR, formatPeriodoBR } from '../utils/dateUtils';
import { exportMedicaoToExcel, exportMedicoesListToExcel, resolveTipoVeiculoDisplay } from '../services/medicaoExportService';
import { fixEncodingIssues } from '../utils/textUtils';

export function MedicoesPage() {
  const [tab, setTab] = useState<'cliente' | 'agregado'>('cliente');
  const [medicoesCliente, setMedicoesCliente] = useState<Medicoes[]>([]);
  const [medicoesAgregado, setMedicoesAgregado] = useState<MedicoesAgregado[]>([]);
  const [statusList, setStatusList] = useState<StatusMedicao[]>([]);
  const [clientes, setClientes] = useState<{ id: number; nome: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastData | null>(null);

  // Filter & Sort State
  const [filters, setFilters] = useState<FilterSortState>({
    searchTerm: '',
    statusFilter: 'all',
    sortBy: 'id',
    sortOrder: 'desc',
    startDate: '',
    endDate: '',
  });

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number; type: 'cliente' | 'agregado'; desc: string } | null>(null);

  // Financial modal state
  const [financeiroModal, setFinanceiroModal] = useState<{
    isOpen: boolean;
    medicao: Medicoes | MedicoesAgregado | null;
    tipo: 'cliente' | 'agregado';
  }>({
    isOpen: false,
    medicao: null,
    tipo: 'cliente',
  });

  const [pageCliente, setPageCliente] = useState(1);
  const [limitCliente, setLimitCliente] = useState(50);
  const [totalCliente, setTotalCliente] = useState(0);

  const [pageAgregado, setPageAgregado] = useState(1);
  const [limitAgregado, setLimitAgregado] = useState(50);
  const [totalAgregado, setTotalAgregado] = useState(0);

  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isAgregadoModalOpen, setIsAgregadoModalOpen] = useState(false);
  const [selectedMedicao, setSelectedMedicao] = useState<Medicoes | null>(null);
  const [selectedAgregado, setSelectedAgregado] = useState<MedicoesAgregado | null>(null);

  // Load auxiliary metadata once
  useEffect(() => {
    let mounted = true;
    const loadMetadata = async () => {
      try {
        const [st, cl] = await Promise.all([
          medicaoService.getStatusMedicao(),
          medicaoService.getClientesMedicao(),
        ]);
        if (mounted) {
          setStatusList(st);
          setClientes(cl);
        }
      } catch (e) {
        console.warn('Erro ao carregar metadados auxiliares de medição:', e);
      }
    };
    loadMetadata();
    return () => {
      mounted = false;
    };
  }, []);

  const fetchClienteData = useCallback(async (page: number, limit: number) => {
    try {
      const res = await medicaoService.getMedicoes(page, limit);
      setMedicoesCliente(res.data || []);
      setTotalCliente(res.count || 0);
    } catch (err) {
      console.error('Error fetching medicoes cliente:', err);
    }
  }, []);

  const fetchAgregadoData = useCallback(async (page: number, limit: number) => {
    try {
      const res = await medicaoService.getMedicoesAgregado(page, limit);
      setMedicoesAgregado(res.data || []);
      setTotalAgregado(res.count || 0);
    } catch (err) {
      console.error('Error fetching medicoes agregado:', err);
    }
  }, []);

  // Fetch tab data based on current pagination
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const loadData = async () => {
      if (tab === 'cliente') {
        await fetchClienteData(pageCliente, limitCliente);
      } else {
        await fetchAgregadoData(pageAgregado, limitAgregado);
      }
      if (isMounted) {
        setLoading(false);
      }
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, [tab, pageCliente, limitCliente, pageAgregado, limitAgregado, fetchClienteData, fetchAgregadoData]);

  // Keep stable refs for realtime trigger
  const stateRef = useRef({ tab, pageCliente, limitCliente, pageAgregado, limitAgregado });
  useEffect(() => {
    stateRef.current = { tab, pageCliente, limitCliente, pageAgregado, limitAgregado };
  }, [tab, pageCliente, limitCliente, pageAgregado, limitAgregado]);

  // Realtime listeners isolated to mount/unmount with debounce
  useEffect(() => {
    let debounceTimer: any = null;

    const handleRealtimeChange = (table: 'medicoes' | 'medicoes_agregado') => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const { tab: currentTab, pageCliente: pC, limitCliente: lC, pageAgregado: pA, limitAgregado: lA } = stateRef.current;
        if (table === 'medicoes' && currentTab === 'cliente') {
          fetchClienteData(pC, lC);
        } else if (table === 'medicoes_agregado' && currentTab === 'agregado') {
          fetchAgregadoData(pA, lA);
        }
      }, 400);
    };

    const channelCliente = supabase
      .channel('medicoes_cliente_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'medicoes' }, () => {
        handleRealtimeChange('medicoes');
      })
      .subscribe();

    const channelAgregado = supabase
      .channel('medicoes_agregado_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'medicoes_agregado' }, () => {
        handleRealtimeChange('medicoes_agregado');
      })
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channelCliente);
      supabase.removeChannel(channelAgregado);
    };
  }, [fetchClienteData, fetchAgregadoData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tab === 'cliente') {
        await fetchClienteData(pageCliente, limitCliente);
      } else {
        await fetchAgregadoData(pageAgregado, limitAgregado);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewClient = () => {
    setSelectedMedicao(null);
    setIsClientModalOpen(true);
  };

  const handleEditClient = (m: Medicoes) => {
    setSelectedMedicao(m);
    setIsClientModalOpen(true);
  };

  const handleEditAgregado = (m: MedicoesAgregado) => {
    setSelectedAgregado(m);
    setIsAgregadoModalOpen(true);
  };

  const handleNewAgregado = () => {
    setSelectedAgregado(null);
    setIsAgregadoModalOpen(true);
  };

  const openDeleteModal = (id: number, type: 'cliente' | 'agregado', desc: string) => {
    setItemToDelete({ id, type, desc });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      if (itemToDelete.type === 'cliente') {
        await medicaoService.deleteMedicao(itemToDelete.id);
      } else {
        await medicaoService.deleteMedicaoAgregado(itemToDelete.id);
      }
      setToast({
        type: 'success',
        title: 'Medição Excluída',
        message: `${itemToDelete.desc} foi excluída com sucesso.`,
      });
      await fetchData();
    } catch (err: any) {
      setToast({
        type: 'error',
        title: 'Erro ao excluir',
        message: err.message || 'Falha ao excluir o registro da medição.',
      });
    } finally {
      setItemToDelete(null);
    }
  };

  const openGerarFinanceiroModal = (medicao: Medicoes | MedicoesAgregado, tipo: 'cliente' | 'agregado') => {
    if (medicao.status_medicao_id === 4) {
      setToast({
        type: 'error',
        title: 'Medição já Faturada',
        message: `A Medição #${medicao.id} já possui lançamento financeiro gerado e não pode ser faturada novamente.`,
      });
      return;
    }
    setFinanceiroModal({
      isOpen: true,
      medicao,
      tipo,
    });
  };

  const [exportingId, setExportingId] = useState<number | null>(null);

  const handleExportSingleMedicao = async (medicao: Medicoes | MedicoesAgregado, type: 'cliente' | 'agregado') => {
    try {
      setExportingId(medicao.id);
      const isAgregado = type === 'agregado';
      const osList = isAgregado
        ? await medicaoService.getOSByMedicaoAgregado(medicao.id)
        : await medicaoService.getOSByMedicaoCliente(medicao.id);

      if (!osList || osList.length === 0) {
        alert('Esta medição não possui Ordens de Serviço vinculadas para exportação.');
        return;
      }

      const clientName = isAgregado
        ? (medicao as any).agregado_nome || (medicao as any).pessoa?.nome
        : ((medicao as any).cliente_nome && (medicao as any).cliente_nome !== '-')
          ? (medicao as any).cliente_nome
          : ((medicao as any).pedido?.pessoa?.nome || clientes.find(c => c.id === (medicao as any).cliente_id)?.nome);

      const st = statusList.find(s => s.id === medicao.status_medicao_id);

      exportMedicaoToExcel(
        {
          id: medicao.id,
          type,
          clienteNome: !isAgregado ? clientName : undefined,
          agregadoNome: isAgregado ? clientName : undefined,
          periodoInicial: medicao.periodo_inicial || (medicao as any).data_inicial,
          periodoFinal: medicao.periodo_final || (medicao as any).data_final,
          valorTotal: medicao.valor_medicao,
          statusNome: st?.nome || 'Em Análise',
        },
        osList.map((os) => {
          const qtdAjud = Number(os.quantidade_ajudante ?? os.qtd_ajudante ?? 0);
          const valAjud = isAgregado
            ? Number(os.ajudante_repasse || 0)
            : Number(os.subtotal_viagem ?? (Number(os.valor_unit_ajud || 0) * qtdAjud) ?? 0);
          const totalVal = isAgregado ? Number(os.valor_total_repasses || 0) : Number(os.total_os || 0);
          const tipoVeiculo = resolveTipoVeiculoDisplay(os);
          const valUnitVeic = Number(
            os.valor_unit_servi ??
            (qtdAjud > 0 ? (totalVal - valAjud) : totalVal) ??
            0
          );
          return {
            id: os.id,
            data_atendimento: os.data_atendimento,
            cliente_nome: !isAgregado ? clientName : (os.cliente_nome || os.pedido?.pessoa?.nome || '-'),
            agregado_nome: isAgregado ? clientName : (os.agregado_nome || '-'),
            motorista_nome: os.veiculos?.motorista?.nome || os.motorista?.nome || os.motorista_nome || '-',
            placa: os.veiculos?.placa || os.placa || '-',
            tipo_veiculo: tipoVeiculo,
            descricao: os.descricao ? fixEncodingIssues(os.descricao) : '',
            valor_unit_servi: valUnitVeic,
            retirada: os.retirada || '',
            entrega: os.entrega || '',
            horario_inicio: os.horario_inicio || '',
            horario_final: os.horario_final || '',
            hora_total: os.hora_total || '',
            quantidade_ajudante: qtdAjud,
            valor_ajudante: valAjud,
            total_os: Number(os.total_os) || 0,
            valor_total_repasses: Number(os.valor_total_repasses) || 0,
            status_nome: os.status?.status || os.status_nome || '',
          };
        })
      );
    } catch (err: any) {
      console.error('Erro ao exportar medição:', err);
      alert('Erro ao exportar medição para Excel: ' + (err?.message || 'Tente novamente.'));
    } finally {
      setExportingId(null);
    }
  };

  const handleExportListExcel = () => {
    try {
      const listToExport = tab === 'cliente' ? filteredCliente : filteredAgregado;
      if (listToExport.length === 0) {
        alert('Nenhuma medição encontrada para exportar.');
        return;
      }
      exportMedicoesListToExcel(listToExport, tab);
    } catch (err: any) {
      console.error('Erro ao exportar lista de medições:', err);
      alert('Erro ao exportar lista para Excel: ' + (err?.message || 'Tente novamente.'));
    }
  };

  // Filter and Sort Cliente
  const filteredCliente = medicoesCliente
    .filter((m) => {
      const term = filters.searchTerm.toLowerCase();
      const clientName = (m.cliente_nome || m.pedido?.pessoa?.nome || '').toLowerCase();
      const matchesSearch =
        !term ||
        m.id.toString().includes(term) ||
        m.ordem_de_servico_id?.toString().includes(term) ||
        clientName.includes(term);

      const matchesStatus =
        filters.statusFilter === 'all' ||
        m.status_medicao_id?.toString() === filters.statusFilter;

      const matchesStartDate = !filters.startDate || ((m.periodo_inicial || m.data_inicial) && (m.periodo_inicial || m.data_inicial)! >= filters.startDate);
      const matchesEndDate = !filters.endDate || ((m.periodo_final || m.data_final) && (m.periodo_final || m.data_final)! <= filters.endDate);

      return matchesSearch && matchesStatus && matchesStartDate && matchesEndDate;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (filters.sortBy === 'id') {
        comparison = a.id - b.id;
      } else if (filters.sortBy === 'valor') {
        comparison = (a.valor_medicao || 0) - (b.valor_medicao || 0);
      } else if (filters.sortBy === 'data') {
        comparison = (a.periodo_inicial || a.data_inicial || '').localeCompare(b.periodo_inicial || b.data_inicial || '');
      } else if (filters.sortBy === 'status') {
        comparison = (a.status_medicao_id || 0) - (b.status_medicao_id || 0);
      }
      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

  // Filter and Sort Agregado
  const filteredAgregado = medicoesAgregado
    .filter((m) => {
      const term = filters.searchTerm.toLowerCase();
      const agregadoName = (m.agregado_nome || '').toLowerCase();
      const matchesSearch =
        !term ||
        m.id.toString().includes(term) ||
        m.agregado_id?.toString().includes(term) ||
        agregadoName.includes(term);

      const matchesStatus =
        filters.statusFilter === 'all' ||
        m.status_medicao_id?.toString() === filters.statusFilter;

      const matchesStartDate = !filters.startDate || (m.data_inicial && m.data_inicial >= filters.startDate);
      const matchesEndDate = !filters.endDate || (m.data_final && m.data_final <= filters.endDate);

      return matchesSearch && matchesStatus && matchesStartDate && matchesEndDate;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (filters.sortBy === 'id') {
        comparison = a.id - b.id;
      } else if (filters.sortBy === 'valor') {
        comparison = (a.valor_medicao || a.valor_repassar || 0) - (b.valor_medicao || b.valor_repassar || 0);
      } else if (filters.sortBy === 'data') {
        comparison = (a.data_inicial || '').localeCompare(b.data_inicial || '');
      } else if (filters.sortBy === 'status') {
        comparison = (a.status_medicao_id || 0) - (b.status_medicao_id || 0);
      }
      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="h-7 w-7 text-red-600" />
            Medições & Faturamento
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Controle de medições quinzenais/mensais de clientes e repasses de agregados.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap items-center gap-2">
          <button
            onClick={() => fetchData()}
            className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors cursor-pointer"
          >
            <RefreshCw className="-ml-1 mr-2 h-4 w-4 text-slate-500" />
            Atualizar
          </button>
          <button
            onClick={handleExportListExcel}
            className="inline-flex items-center justify-center rounded-lg border border-emerald-300 bg-white hover:bg-emerald-50 px-3.5 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition-colors cursor-pointer"
            title="Exportar listagem filtrada para Excel"
          >
            <FileSpreadsheet className="-ml-1 mr-2 h-4 w-4 text-emerald-600" />
            Exportar Lista
          </button>
          <button
            onClick={() => (tab === 'cliente' ? handleNewClient() : handleNewAgregado())}
            type="button"
            className="inline-flex items-center justify-center rounded-lg border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 transition-colors cursor-pointer font-semibold shadow-red-200"
          >
            <Plus className="-ml-1 mr-2 h-5 w-5" />
            {tab === 'cliente' ? 'Gerar Medição de Cliente' : 'Gerar Medição de Agregado'}
          </button>
        </div>
      </div>

      {/* Filter and Sort Bar */}
      <FiltroEClassificacaoBar
        filters={filters}
        onChange={setFilters}
        statusOptions={[
          { value: 'all', label: 'Todos os Status' },
          ...statusList.map((s) => ({ value: s.id.toString(), label: s.nome })),
        ]}
        sortOptions={[
          { value: 'id', label: 'Código (ID)' },
          { value: 'valor', label: 'Valor da Medição' },
          { value: 'data', label: 'Data Período' },
          { value: 'status', label: 'Status' },
        ]}
        placeholderSearch={
          tab === 'cliente'
            ? 'Pesquisar por ID, Pedido, Cliente...'
            : 'Pesquisar por ID, Agregado...'
        }
        totalResults={tab === 'cliente' ? filteredCliente.length : filteredAgregado.length}
      />

      {/* Tabs selector */}
      <div className="border-b border-slate-200 flex gap-4">
        <button
          onClick={() => setTab('cliente')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors ${
            tab === 'cliente'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Medições de Clientes ({totalCliente || medicoesCliente.length})
        </button>
        <button
          onClick={() => setTab('agregado')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition-colors ${
            tab === 'agregado'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Medições de Agregados ({totalAgregado || medicoesAgregado.length})
        </button>
      </div>

      <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-slate-200">
        {tab === 'cliente' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Período
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Status Medição
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Valor Medido
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-sm text-slate-500">
                      Carregando medições...
                    </td>
                  </tr>
                ) : filteredCliente.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-sm text-slate-500">
                      Nenhuma medição encontrada.
                    </td>
                  </tr>
                ) : (
                  filteredCliente.map((m) => {
                    const st = statusList.find((s) => s.id === m.status_medicao_id);
                    const clientName = (m.cliente_nome && m.cliente_nome !== '-') 
                      ? m.cliente_nome 
                      : (m.pedido?.pessoa?.nome || '-');
                    const statusName = st?.nome 
                      ? cleanStatusMedicaoName(st.nome) 
                      : (m.status_medicao_id === 1 ? 'Em Análise' 
                       : m.status_medicao_id === 2 ? 'Aprovado' 
                       : m.status_medicao_id === 3 ? 'Reprovado' 
                       : m.status_medicao_id === 4 ? 'Faturado' 
                       : `Status ${m.status_medicao_id}`);
                    const statusColor = st?.cor || (
                      statusName.includes('Análise') ? '#d97706' 
                      : statusName.includes('Aprov') ? '#16a34a' 
                      : statusName.includes('Reprov') ? '#dc2626' 
                      : '#2563eb'
                    );
                    return (
                      <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">#{m.id}</td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-800">
                          {clientName}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {formatPeriodoBR(m.periodo_inicial || m.data_inicial, m.periodo_final || m.data_final)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className="px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center"
                            style={{
                              backgroundColor: `${statusColor}20`,
                              color: statusColor,
                              border: `1px solid ${statusColor}40`,
                            }}
                          >
                            {statusName}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-bold text-slate-900">
                          <MaskedValue
                            permission="view_medicoes_valores"
                            value={m.valor_medicao}
                            type="currency"
                          />
                        </td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <button 
                            id={`btn-excel-medicao-${m.id}`}
                            onClick={() => handleExportSingleMedicao(m, 'cliente')}
                            disabled={exportingId === m.id}
                            className="text-emerald-600 hover:text-emerald-900 p-1.5 rounded-md hover:bg-emerald-50 transition-colors cursor-pointer inline-flex items-center disabled:opacity-40"
                            title="Exportar Medição para Excel (XLS)"
                          >
                            {exportingId === m.id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                            ) : (
                              <FileSpreadsheet className="h-4 w-4" />
                            )}
                          </button>
                          <ProtectedField permission="manage_medicoes">
                            <button 
                              id={`btn-edit-medicao-${m.id}`}
                              onClick={() => handleEditClient(m)}
                              className="text-blue-600 hover:text-blue-900 p-1.5 rounded-md hover:bg-blue-50 transition-colors cursor-pointer inline-flex items-center"
                              title="Editar Medição"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </ProtectedField>
                          <ProtectedField permission="delete_medicoes">
                            <button 
                              id={`btn-delete-medicao-${m.id}`}
                              onClick={() => openDeleteModal(m.id, 'cliente', `Medição Cliente #${m.id} - ${clientName}`)}
                              className="text-red-600 hover:text-red-900 p-1.5 rounded-md hover:bg-red-50 transition-colors cursor-pointer inline-flex items-center"
                              title="Excluir Medição"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </ProtectedField>
                          <ProtectedField permission="manage_financial">
                            {m.status_medicao_id === 4 ? (
                              <span 
                                id={`badge-faturado-cliente-${m.id}`}
                                className="text-blue-700 bg-blue-50 border border-blue-200 p-1.5 rounded-md inline-flex items-center"
                                title="Financeiro já gerado para esta medição (Faturado)"
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </span>
                            ) : (
                              <button 
                                id={`btn-financeiro-medicao-${m.id}`}
                                onClick={() => openGerarFinanceiroModal(m, 'cliente')}
                                className="text-emerald-600 hover:text-emerald-900 p-1.5 rounded-md hover:bg-emerald-50 transition-colors cursor-pointer inline-flex items-center"
                                title="Gerar Lançamento Financeiro (Contas a Receber)"
                              >
                                <DollarSign className="h-4 w-4" />
                              </button>
                            )}
                          </ProtectedField>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            <Pagination
              currentPage={pageCliente}
              totalItems={totalCliente}
              itemsPerPage={limitCliente}
              onPageChange={setPageCliente}
              onItemsPerPageChange={(newLimit) => {
                setLimitCliente(newLimit);
                setPageCliente(1);
              }}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Nº Medição
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Agregado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Período Inicial
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Período Final
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Valor Medição
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-sm text-slate-500">
                      Carregando medições de agregados...
                    </td>
                  </tr>
                ) : filteredAgregado.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-sm text-slate-500">
                      Nenhuma medição de agregado encontrada.
                    </td>
                  </tr>
                ) : (
                  filteredAgregado.map((m) => {
                    const st = statusList.find((s) => s.id === m.status_medicao_id);
                    const statusName = st?.nome 
                      ? cleanStatusMedicaoName(st.nome) 
                      : (m.status_medicao_id === 1 ? 'Em Análise' 
                       : m.status_medicao_id === 2 ? 'Aprovado' 
                       : m.status_medicao_id === 3 ? 'Reprovado' 
                       : m.status_medicao_id === 4 ? 'Faturado' 
                       : `Status ${m.status_medicao_id}`);
                    const statusColor = st?.cor || (
                      statusName.includes('Análise') ? '#d97706' 
                      : statusName.includes('Aprov') ? '#16a34a' 
                      : statusName.includes('Reprov') ? '#dc2626' 
                      : '#2563eb'
                    );
                    return (
                      <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">#{m.id}</td>
                        <td className="px-6 py-4">
                          <span
                            className="px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center"
                            style={{
                              backgroundColor: `${statusColor}20`,
                              color: statusColor,
                              border: `1px solid ${statusColor}40`,
                            }}
                          >
                            {statusName}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 font-semibold">
                          {m.agregado_nome || `Agregado #${m.agregado_id}`}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {formatDateBR(m.periodo_inicial || m.data_inicial)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {formatDateBR(m.periodo_final || m.data_final)}
                        </td>
                        <td className="px-6 py-4 text-sm font-bold text-slate-900">
                          <MaskedValue
                            permission="view_medicoes_valores"
                            value={m.valor_medicao}
                            type="currency"
                          />
                        </td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <button 
                            id={`btn-excel-agregado-${m.id}`}
                            onClick={() => handleExportSingleMedicao(m, 'agregado')}
                            disabled={exportingId === m.id}
                            className="text-emerald-600 hover:text-emerald-900 p-1.5 rounded-md hover:bg-emerald-50 transition-colors cursor-pointer inline-flex items-center disabled:opacity-40"
                            title="Exportar Medição para Excel (XLS)"
                          >
                            {exportingId === m.id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                            ) : (
                              <FileSpreadsheet className="h-4 w-4" />
                            )}
                          </button>
                          <ProtectedField permission="manage_medicoes">
                            <button 
                              id={`btn-edit-agregado-${m.id}`}
                              onClick={() => handleEditAgregado(m)}
                              className="text-blue-600 hover:text-blue-900 p-1.5 rounded-md hover:bg-blue-50 transition-colors cursor-pointer inline-flex items-center"
                              title="Editar Medição"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </ProtectedField>
                          <ProtectedField permission="delete_medicoes">
                            <button 
                              id={`btn-delete-agregado-${m.id}`}
                              onClick={() => openDeleteModal(m.id, 'agregado', `Medição Agregado #${m.id} - ${m.agregado_nome || m.agregado_id}`)}
                              className="text-red-600 hover:text-red-900 p-1.5 rounded-md hover:bg-red-50 transition-colors cursor-pointer inline-flex items-center"
                              title="Excluir Medição"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </ProtectedField>
                          <ProtectedField permission="manage_financial">
                            {m.status_medicao_id === 4 ? (
                              <span 
                                id={`badge-faturado-agregado-${m.id}`}
                                className="text-blue-700 bg-blue-50 border border-blue-200 p-1.5 rounded-md inline-flex items-center"
                                title="Financeiro já gerado para esta medição (Faturado)"
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </span>
                            ) : (
                              <button 
                                id={`btn-financeiro-agregado-${m.id}`}
                                onClick={() => openGerarFinanceiroModal(m, 'agregado')}
                                className="text-emerald-600 hover:text-emerald-900 p-1.5 rounded-md hover:bg-emerald-50 transition-colors cursor-pointer inline-flex items-center"
                                title="Gerar Lançamento Financeiro"
                              >
                                <DollarSign className="h-4 w-4" />
                              </button>
                            )}
                          </ProtectedField>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            <Pagination
              currentPage={pageAgregado}
              totalItems={totalAgregado}
              itemsPerPage={limitAgregado}
              onPageChange={setPageAgregado}
              onItemsPerPageChange={(newLimit) => {
                setLimitAgregado(newLimit);
                setPageAgregado(1);
              }}
            />
          </div>
        )}
      </div>

      <MedicaoFormModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        onSuccess={fetchData}
        selectedMedicao={selectedMedicao}
      />

      <MedicaoAgregadoFormModal
        isOpen={isAgregadoModalOpen}
        onClose={() => setIsAgregadoModalOpen(false)}
        onSuccess={fetchData}
        selectedMedicao={selectedAgregado}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão de Medição"
        itemDescription={itemToDelete?.desc}
      />

      {financeiroModal.medicao && (
        <GerarFinanceiroModal
          isOpen={financeiroModal.isOpen}
          onClose={() => setFinanceiroModal(prev => ({ ...prev, isOpen: false }))}
          onSuccess={(msg) => {
            setToast({
              type: 'success',
              title: 'Financeiro Gerado',
              message: msg,
            });
            fetchData();
          }}
          medicao={financeiroModal.medicao}
          tipo={financeiroModal.tipo}
          clienteId={
            financeiroModal.tipo === 'cliente'
              ? (financeiroModal.medicao as Medicoes).cliente_id || (financeiroModal.medicao as Medicoes).pedido?.pessoa?.id || undefined
              : (financeiroModal.medicao as MedicoesAgregado).agregado_id || undefined
          }
          clienteNome={
            financeiroModal.tipo === 'cliente'
              ? (financeiroModal.medicao as Medicoes).cliente_nome || (financeiroModal.medicao as Medicoes).pedido?.pessoa?.nome || 'Cliente'
              : (financeiroModal.medicao as MedicoesAgregado).agregado_nome || `Agregado #${(financeiroModal.medicao as MedicoesAgregado).agregado_id}`
          }
          valorTotal={
            financeiroModal.tipo === 'cliente'
              ? (financeiroModal.medicao as Medicoes).valor_medicao || 0
              : (financeiroModal.medicao as MedicoesAgregado).valor_medicao || (financeiroModal.medicao as MedicoesAgregado).valor_repassar || 0
          }
        />
      )}

      {/* Floating Toast Feedback */}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
